import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { parseServerTime } from "../../../utils/serverTime";

/**
 * 최고 관리자(ADMIN) 전용 — 하루치 raw 전류(WE1=we_current, WE2=ae_current) 차트.
 * props.dayData = { times: [ISO UTC '…Z' 또는 KST 'yyyy-MM-dd HH:mm:ss', ...], we: [...], ae: [...] }
 * x축은 실제 순간(epoch) + datetimeUTC:false → 브라우저 시간대로 표시(viewer-local, UTC-first 2026-09-22)
 * props.graphXminutes = 그래프 간격(분)
 * x축 로컬→UTC 변환·0시/23:59 앵커는 AnnotationChart와 동일 정책.
 */
const RawCurrentChart = (props) => {
  const [weSeries, setWeSeries] = useState([]);
  const [aeSeries, setAeSeries] = useState([]);

  useEffect(() => {
    if (!props.dayData || !props.dayData.times || props.dayData.times.length === 0) {
      setWeSeries([]);
      setAeSeries([]);
      return;
    }

    // 서버 시각(ISO UTC/KST 문자열) → 실제 순간. 옛 "벽시계를 Date.UTC 로 위장" 트릭 제거
    const toInstant = (str) => parseServerTime(str);

    const we = [];
    const ae = [];
    const step = props.graphXminutes || 1;

    for (let i = 0; i < props.dayData.times.length; i += step) {
      const m = toInstant(props.dayData.times[i]);
      if (m === null || !m.isValid()) {
        continue;
      }
      const x = m.valueOf();

      //그래프 맨 앞에 (브라우저 로컬) 0시 앵커 (하루 폭 유지)
      if (i === 0) {
        const zero = m.clone().startOf('day').valueOf();
        if (zero < x) {
          we.push({ x: zero, y: null });
          ae.push({ x: zero, y: null });
        }
      }

      we.push({ x: x, y: Math.round(props.dayData.we[i] * 100) / 100 });
      ae.push({ x: x, y: Math.round(props.dayData.ae[i] * 100) / 100 });
    }

    //그래프 뒤를 (브라우저 로컬) 23:59:59 앵커로 채워 하루 폭 유지
    const last = toInstant(props.dayData.times[props.dayData.times.length - 1]);
    if (last !== null && last.isValid()) {
      const tail = last.clone().endOf('day').valueOf();
      we.push({ x: tail, y: null });
      ae.push({ x: tail, y: null });
    }

    setWeSeries(we);
    setAeSeries(ae);
  }, [props.dayData, props.graphXminutes]);

  const options = {
    chart: {
      id: 'raw-current-line',
      type: 'line',
      animations: {
        enabled: false
      },
      zoom: {
        type: 'xy',
        enabled: true
      }
    },
    xaxis: {
      type: 'datetime',
      labels: {
        datetimeUTC: false // x는 실제 순간 → 브라우저 시간대로 라벨
      },
      tooltip: {
        enabled: false
      }
    },
    stroke: {
      curve: 'smooth',
      width: 1
    },
    markers: {
      size: 0
    },
    //표시 중인 계열 순서와 색을 맞춘다 (WE1=파랑, WE2=보라)
    colors: [
      ...(props.showWe1 !== false ? ['#1976D2'] : []),
      ...(props.showWe2 !== false ? ['#9C27B0'] : [])
    ],
    legend: {
      show: true,
      position: 'top'
    },
    yaxis: {
      //데이터 범위에 자동 확대되면 미세한 차이가 극단적으로 벌어져 보임 → 0부터 시작
      min: 0,
      forceNiceScale: true,
      title: { text: 'nA' },
      decimalsInFloat: 2
    },
    tooltip: {
      enabled: true,
      x: {
        format: 'MM/dd HH:mm'
      },
      followCursor: true
    }
  };

  const series = [
    ...(props.showWe1 !== false ? [{ name: 'WE1', data: weSeries }] : []),
    ...(props.showWe2 !== false ? [{ name: 'WE2', data: aeSeries }] : [])
  ];

  return (
    <ReactApexChart options={options} series={series} type="line" height={170} />
  );
};

export default RawCurrentChart;
